import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Link } from 'react-router-dom';

import styles from './JobCard.module.scss';
import { Briefcase, CircleCheckBig, Clock, Eye, UserCheck } from 'lucide-react';
import StatusBadge from 'components/ClientATS/StatusBadge/StatusBadge';
import RoleLabel from 'components/ClientATS/RoleLabel/RoleLabel';

const formatPostedDate = (timestamp) => {
  if (!timestamp) return '';
  return `Posted ${moment(timestamp).format('MMM D, YYYY')}`;
};

const JobCard = ({ brief }) => {
  let status = brief?.status;

  if (
    (status === 'expired' || status === 'inactive') &&
    brief?.stats?.active_hires > 0
  ) {
    // Sets any cards where the brief is expired or inactive but has active hires to active
    status = 'active';
  } else if (status === 'active' && brief?.stats?.active_hires === 0) {
    // Sets any cards where the brief is active but has no active hires to open
    status = 'open';
  }

  const baseUrl = brief.links?.ats_main_relative || '#';

  const handleStatClick = (e) => {
    e.stopPropagation();
  };

  return (
    <Link to={baseUrl} className={styles.jobCard}>
      <div className={styles.jobCardContent}>
        <div className={styles.jobCardHeader}>
          <div className={styles.jobCardHeaderContent}>
            <h3 className={styles.jobCardTitle}>{brief.text}</h3>
            {brief.role && (
              <div className={styles.jobCardRole}>
                <RoleLabel role={brief.role} />
              </div>
            )}
            <div className={styles.jobCardMeta}>
              <Clock width={16} height={16} />
              <span>{formatPostedDate(brief.timestamp)}</span>
            </div>
          </div>
          <StatusBadge status={status} />
        </div>
        <p className={styles.jobCardDescription}>{brief.description}</p>
      </div>
      <div className={styles.jobCardStats}>
        <Link
          to={`${baseUrl}?tab=applied`}
          className={styles.jobCardStat}
          onClick={handleStatClick}
        >
          <div className={`${styles.jobCardStatIcon} ${styles.iconBlue}`}>
            <Briefcase width={16} height={16} />
          </div>
          <div>
            <div className={styles.jobCardStatLabel}>Applied</div>
            <div className={styles.jobCardStatValue}>
              {brief.stats?.applied || 0}
            </div>
          </div>
        </Link>
        <Link
          to={`${baseUrl}?tab=reviewed`}
          className={styles.jobCardStat}
          onClick={handleStatClick}
        >
          <div className={`${styles.jobCardStatIcon} ${styles.iconOrange}`}>
            <Eye width={16} height={16} />
          </div>
          <div>
            <div className={styles.jobCardStatLabel}>Reviewed</div>
            <div className={styles.jobCardStatValue}>
              {brief.stats?.reviewed || 0}
            </div>
          </div>
        </Link>
        <Link
          to={`${baseUrl}?tab=shortlisted`}
          className={styles.jobCardStat}
          onClick={handleStatClick}
        >
          <div className={`${styles.jobCardStatIcon} ${styles.iconPurple}`}>
            <UserCheck width={16} height={16} />
          </div>
          <div>
            <div className={styles.jobCardStatLabel}>Shortlisted</div>
            <div className={styles.jobCardStatValue}>
              {brief.stats?.shortlisted || 0}
            </div>
          </div>
        </Link>
        <Link
          to={`${baseUrl}?tab=hired`}
          className={styles.jobCardStat}
          onClick={handleStatClick}
        >
          <div className={`${styles.jobCardStatIcon} ${styles.iconGreen}`}>
            <CircleCheckBig width={16} height={16} />
          </div>
          <div>
            <div className={styles.jobCardStatLabel}>Hired</div>
            <div className={styles.jobCardStatValue}>
              {brief.stats?.hired || 0}
            </div>
          </div>
        </Link>
      </div>
    </Link>
  );
};

JobCard.propTypes = {
  brief: PropTypes.shape({
    id: PropTypes.number,
    text: PropTypes.string,
    description: PropTypes.string,
    timestamp: PropTypes.string,
    status: PropTypes.string,
    role: PropTypes.string,
    links: PropTypes.shape({
      ats_main_relative: PropTypes.string,
    }),
    stats: PropTypes.shape({
      invited: PropTypes.number,
      applied: PropTypes.number,
      reviewed: PropTypes.number,
      shortlisted: PropTypes.number,
      declined: PropTypes.number,
      hired: PropTypes.number,
      active_hires: PropTypes.number,
    }),
  }).isRequired,
};

export default JobCard;
